import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { faFileText, faFolder } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonStandard, ModalBase, Skeleton, Text } from "@performyard/py-components";

import { useCurrentUser } from "../../../../api/hooks";
import { useDocuments } from "../../../../api/hooks/defs/documents";

import {
	type PickableDocument,
	getCurrentUserName,
	toPickableDocuments,
	useFolderAccordion,
} from "../../../Documents/documentPicker";

export type SignatureRequestDocumentPickerProps = {
	isOpen: boolean;
	onClose: () => void;
};

type DocumentFolder = {
	key: string;
	label: string;
	documents: PickableDocument[];
};

const SignatureRequestDocumentPicker = ({ isOpen, onClose }: SignatureRequestDocumentPickerProps) => {
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [isAttachOpen, setIsAttachOpen] = useState(false);
	const { isExpanded: isFolderExpanded, toggle, collapseAll } = useFolderAccordion();

	const navigate = useNavigate();

	const { data: currentUser } = useCurrentUser();
	const userId = currentUser?.me?.authorization?.payload?.user_id ?? "";
	const userName = getCurrentUserName();

	const { data: documentsData, isLoading } = useDocuments(isOpen ? userId : "");

	const folders: DocumentFolder[] = [
		{
			key: "company",
			label: "Company Documents",
			documents: toPickableDocuments(documentsData?.companyFiles ?? []),
		},
		{
			key: "my",
			label: userName ? `${userName}'s Documents` : "My Documents",
			documents: toPickableDocuments(documentsData?.myFiles ?? []),
		},
	].filter((folder) => folder.documents.length > 0);

	const hasDocuments = folders.length > 0;

	const toggleFolder = (key: string) => {
		setSelectedId(null);
		toggle(key);
	};

	const handleClose = () => {
		setSelectedId(null);
		setIsAttachOpen(false);
		collapseAll();
		onClose();
	};

	const handleContinue = () => {
		if (!selectedId) return;
		navigate(`/doc-signing/templates/${selectedId}/setup/`);
	};

	const renderBody = () => {
		if (isLoading) {
			return (
				<div className="flex flex-col gap-2">
					{[0, 1, 2, 3].map((i) => (
						<Skeleton key={i} className="h-10 w-full" />
					))}
				</div>
			);
		}

		if (!hasDocuments) {
			return (
				<div className="flex h-24 items-center justify-center">
					<Text size="paragraph-3" dangerouslySetClassName="text-neutral-6">
						No PDF documents found. Upload a PDF document first.
					</Text>
				</div>
			);
		}

		return (
			<div className="flex flex-col border border-neutral-1">
				{folders.map((folder) => {
					const expanded = isFolderExpanded(folder.key);
					return (
						<div key={folder.key}>
							<button
								type="button"
								aria-expanded={expanded}
								onClick={() => toggleFolder(folder.key)}
								className="flex w-full items-center gap-2 border-transparent bg-neutral-1 px-3 py-2 text-left hover:cursor-pointer"
							>
								<FontAwesomeIcon icon={faFolder} className="text-primary-5" />
								<Text size="paragraph-3" weight="regular" dangerouslySetClassName="text-neutral-7">
									{folder.label}
								</Text>
							</button>

							{expanded && (
								<div className="flex flex-col gap-1 pb-2">
									{folder.documents.map((doc) => {
										const isSelected = doc.id === selectedId;
										return (
											<button
												key={doc.id}
												type="button"
												aria-pressed={isSelected}
												onClick={() => setSelectedId(doc.id)}
												className={`flex items-center gap-2 rounded-sm border px-3 py-2 ps-8 text-left hover:cursor-pointer ${
													isSelected
														? "border-transparent bg-primary-5"
														: "border-transparent bg-white"
												}`}
											>
												<Text size="paragraph-3" weight="regular">
													<span
														className={`${isSelected ? "text-white" : "text-neutral-5 hover:text-neutral-7"} py-line-clamp-1 hover:cursor-pointer`}
													>
														{doc.name}
													</span>
												</Text>
											</button>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={handleClose}>
			<div className="box-border w-[480px] px-6 pb-6">
				<button
					type="button"
					aria-expanded={isAttachOpen}
					onClick={() => setIsAttachOpen((prev) => !prev)}
					className="mt-4 flex w-full items-center justify-center gap-2 border border-neutral-1 bg-neutral-1 px-3 py-2 hover:cursor-pointer"
				>
					<FontAwesomeIcon icon={faFileText} className="text-primary-5" />
					<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="text-neutral-7">
						Attach Document Signing
					</Text>
				</button>

				{isAttachOpen && <div className="max-h-[320px] overflow-y-auto py-3">{renderBody()}</div>}

				<div className="mt-6 flex justify-end gap-2">
					<div>
						<ButtonStandard size="medium" variant="outline" color="neutral" onClick={handleClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div>
						<ButtonStandard size="medium" color="primary" disabled={!selectedId} onClick={handleContinue}>
							Add Document
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};

export default SignatureRequestDocumentPicker;
